import { Ref, useRef } from "react";
import { CreateOrderWizard, CreateOrderDeliveryStep } from "../helpers/createOrderHelpers";
import { Controller, useForm } from "react-hook-form";
import { Divider, Grid } from "@mantine/core";
import { DeliveryStepInput, DeliveryValidationSchema } from "./Validation/DeliveryValidation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";
import TextInput from "@/components/atoms/TextInput";
import DatePickerInput from "@/components/atoms/DatePickerInput";
import Checkbox from "@/components/atoms/Checkbox";
import SelectCountry from "@/components/molecules/SelectInputs/SelectCountry";
import SelectShippingAddress from "@/components/molecules/SelectInputs/SelectShippingAddress";
import OrderTypeEnum from "@/constants/enums/OrderTypeEnum";
import { useAuth } from "@/contexts/AuthContext";
import { useGetShippingAddresses } from "@/services/Company";
import { useGetCountries } from "@/services/RegisterCompany";
import Text from "@/components/atoms/Text";
import { findDeliveryAddress } from "@/utils/AddressHelper";
import CoordinateFields from "@/components/molecules/CoordinateFields";

interface Props {
  onValidationSuccess: (data: CreateOrderDeliveryStep) => void;
  onChangeWizardData: (data: CreateOrderDeliveryStep) => void;
  wizardData: CreateOrderWizard;
  ref?: Ref<HTMLFormElement>;
}

function Delivery({ onValidationSuccess, onChangeWizardData, wizardData, ref }: Props) {
  const { t } = useTranslation();
  const { currentCompany, user } = useAuth();
  const differentAddressNodeRef = useRef(null);

  const { data: shippingAddresses } = useGetShippingAddresses(currentCompany?.companyId as number, {
    enabled: !!currentCompany?.companyId,
  });
  const defaultShippingAddress = shippingAddresses?.find((a) => a.isDefault) ?? shippingAddresses?.[0];

  const { data: countries } = useGetCountries(user?.language?.id ?? 1, {
    enabled: !!user?.language?.id,
  });

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<DeliveryStepInput>({
    mode: "onSubmit",
    resolver: zodResolver(DeliveryValidationSchema),
    defaultValues: { ...wizardData },
  });

  const watchedDifferent = watch("differentAddress");
  const watchedAddress = watch("address");
  const watchedPostCode = watch("postCode");
  const watchedPlace = watch("place");
  const watchedCountryId = watch("countryId");

  const watchedCountryName = countries?.find((c) => String(c.id) === watchedCountryId)?.name ?? "";

  const displayAddressLine =
    watchedDifferent && watchedAddress
      ? `${watchedAddress}, ${watchedPostCode ?? ""} ${watchedPlace ?? ""}, ${watchedCountryName}`
      : defaultShippingAddress
        ? findDeliveryAddress([defaultShippingAddress])
        : undefined;

  const onSubmit = (data: DeliveryStepInput) => {
    // When the user keeps the pre-selected default address, the address fields are never
    // populated (they're only wired up via the "different address" picker) — fill them in
    // from the default shipping address here so the order is actually saved with an address.
    const resolvedData: DeliveryStepInput =
      data.differentAddress || !defaultShippingAddress
        ? data
        : {
            ...data,
            address: defaultShippingAddress.address,
            postCode: defaultShippingAddress.postCode ?? "",
            place: defaultShippingAddress.place,
            countryId: String(defaultShippingAddress.country.id),
          };
    onChangeWizardData(resolvedData);
    onValidationSuccess(resolvedData);
  };

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <Grid.Col span={6}>
          <Controller
            control={control}
            name="validFrom"
            render={({ field: { onChange, value } }) => (
              <DatePickerInput label={t("common:validFrom")} withAsterisk onChange={onChange} value={value} />
            )}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Controller
            control={control}
            name="validTo"
            render={({ field: { onChange, value } }) => (
              <DatePickerInput label={t("common:validTo")} withAsterisk value={value} onChange={onChange} />
            )}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          {wizardData.orderType === OrderTypeEnum.BUY ? (
            <Controller
              control={control}
              name="lastDeliveryDate"
              render={({ field: { onChange, value } }) => (
                <DatePickerInput label={t("orders:lastDeliveryDate")} value={value} onChange={onChange} />
              )}
            />
          ) : null}
        </Grid.Col>

        <Grid.Col span={12}>
          <Divider />
        </Grid.Col>

        {displayAddressLine && (
          <Grid.Col span={12}>
            <Text color="secondary" size="sm">
              {t("orders:orderAddress")}
            </Text>
            <Text size="lg" strong={true}>
              {displayAddressLine}
            </Text>
          </Grid.Col>
        )}

        <Grid.Col span={12}>
          <Controller
            control={control}
            name="differentAddress"
            render={({ field: { onChange, value } }) => (
              <Checkbox
                label={t("contracts:differentAddress")}
                checked={value}
                onChange={(e) => {
                  onChange(e.target.checked);
                }}
              />
            )}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <CSSTransition
            in={watch("differentAddress")}
            timeout={{
              enter: 1000,
              exit: 300,
            }}
            mountOnEnter
            unmountOnExit
            nodeRef={differentAddressNodeRef}
          >
            <Grid>
              <Grid.Col span={12}>
                <SelectShippingAddress
                  companyId={currentCompany?.companyId}
                  label={t("common:shippingAddress")}
                  placeholder={t("common:shippingAddress")}
                  autoSelectDefault={false}
                  setSelectedAddress={(address) => {
                    setValue("address", address.address, { shouldValidate: true, shouldDirty: true });
                    setValue("postCode", address.postCode ?? "", { shouldValidate: true, shouldDirty: true });
                    setValue("place", address.place, { shouldValidate: true, shouldDirty: true });
                    setValue("countryId", String(address.country.id), { shouldValidate: true, shouldDirty: true });
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Controller
                  control={control}
                  name="address"
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      id="address-order-from-contract"
                      label={t("orders:orderAddress")}
                      withAsterisk
                      disabled={!watch("differentAddress")}
                      value={value}
                      onChange={onChange}
                      error={errors.address ? t(`validation:${errors.address.message}`) : undefined}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Controller
                  control={control}
                  name="countryId"
                  render={({ field: { onChange, value } }) => (
                    <SelectCountry
                      id="country-order-from-contract"
                      label={t("common:country")}
                      withAsterisk
                      value={value}
                      onChange={(e: any) => {
                        onChange(e);
                      }}
                      valueKey={"id"}
                      labelKey={"name"}
                      error={errors.countryId ? t(`validation:${errors.countryId.message}`) : undefined}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Controller
                  control={control}
                  name="postCode"
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      id="postCode-order-from-contract"
                      label={t("orders:postCode")}
                      withAsterisk
                      disabled={!watch("differentAddress")}
                      value={value}
                      onChange={onChange}
                      error={errors.postCode ? t(`validation:${errors.postCode.message}`) : undefined}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Controller
                  control={control}
                  name="place"
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      id="postPlace"
                      label={t("orders:place")}
                      withAsterisk
                      disabled={!watch("differentAddress")}
                      value={value}
                      onChange={onChange}
                      error={errors.place ? t(`validation:${errors.place.message}`) : undefined}
                    />
                  )}
                />
              </Grid.Col>

              <Grid.Col span={12}>
                <Divider />
              </Grid.Col>
            </Grid>
          </CSSTransition>
        </Grid.Col>
        <Grid.Col span={12}>
          <CoordinateFields
            control={control}
            latitudeName="latitude"
            longitudeName="longitude"
            setValue={setValue}
            onCaptured={(capturedAt) => setValue("capturedAt", capturedAt)}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <Controller
            control={control}
            name="description"
            render={({ field: { onChange, value } }) => (
              <TextInput label={t("common:description")} value={value ?? ""} onChange={onChange} />
            )}
          />
        </Grid.Col>
        {/* <Grid.Col span={12}>
          <Select label={t("common:companyToTradeWith")} />
        </Grid.Col> */}
      </Grid>
    </form>
  );
}

export default Delivery;
